'use client'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { KEY_STATS, MORE_FACTS, SOURCES, type KeyStat } from '@/data/japan-stats'
import { ForeignWorkersChart, WorkingAgeChart } from './JapanCharts'
import { useCountUp, useInView } from './useInView'

type Locale = 'en' | 'ja'

const T = {
  en: {
    eyebrow: 'Why Japan, why now',
    title: 'Japan is short of workers. Pakistan is full of talent.',
    lead: 'Japan’s population is ageing fast and its workforce is shrinking. That is why it keeps opening new visa routes for skilled foreign workers, and why Japanese language is now a career skill, not a hobby.',
    c1: 'Foreign workers in Japan keep breaking records',
    c1s: 'Number of foreign workers, end of October each year',
    c2: 'Japan’s working-age population is shrinking',
    c2s: 'People aged 15–64, millions (dashed = official projection)',
    sources: 'Sources',
    cta: 'See how to work in Japan',
  },
  ja: {
    eyebrow: 'なぜ今、日本なのか',
    title: '日本は人手不足。パキスタンには人材がいる。',
    lead: '日本は高齢化が急速に進み、働き手が減り続けています。だからこそ外国人材の受け入れ制度を広げており、日本語は趣味ではなく「キャリアのスキル」になりました。',
    c1: '日本で働く外国人は過去最多を更新中',
    c1s: '外国人労働者数（各年10月末）',
    c2: '日本の生産年齢人口は減少',
    c2s: '15〜64歳の人口（点線は公式の将来推計）',
    sources: '出典',
    cta: '日本で働く方法を見る',
  },
}

function StatTile({ s, locale, i }: { s: KeyStat; locale: Locale; i: number }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, 0.4)
  const v = useCountUp(s.value[locale], inView, s.decimals[locale], 1400 + i * 150)
  const shown = locale === 'ja' && s.value.ja >= 1000 ? v.toLocaleString('ja-JP') : v.toFixed(s.decimals[locale])
  return (
    <div
      ref={ref}
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.06] p-6 backdrop-blur-sm transition-colors hover:border-brand-gold/50"
    >
      <span className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/70 to-transparent" aria-hidden />
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-gold">{s.note[locale]}</p>
      <p className="mt-3 text-5xl font-bold leading-none tracking-tight text-white tabular-nums sm:text-[3.4rem]">
        {shown}
        <span className="ml-0.5 text-3xl text-white/80 sm:text-4xl">{s.suffix[locale]}</span>
      </p>
      <p className="mt-3 text-sm leading-relaxed text-white/75">{s.label[locale]}</p>
      <a href={SOURCES[s.source].url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-[11px] text-white/45 underline-offset-2 hover:text-brand-gold hover:underline">
        {SOURCES[s.source].name}
      </a>
    </div>
  )
}

export default function JapanNeeds({ locale = 'en' }: { locale?: Locale }) {
  const t = T[locale]
  return (
    <section id="why-japan" className="relative isolate overflow-hidden bg-brand-navy py-20 text-white sm:py-24">
      <div className="ncp-asanoha absolute inset-0 -z-10" aria-hidden />
      <div className="absolute -left-40 top-10 -z-10 h-96 w-96 rounded-full bg-brand-green/50 blur-3xl" aria-hidden />
      <div className="absolute -right-40 bottom-0 -z-10 h-96 w-96 rounded-full bg-brand-red/30 blur-3xl" aria-hidden />
      <p
        className="pointer-events-none absolute right-4 top-16 -z-10 hidden font-[family-name:var(--font-noto-serif-jp)] text-[9rem] font-bold leading-none text-white/[0.04] [writing-mode:vertical-rl] lg:block"
        aria-hidden
      >
        人手不足
      </p>

      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="max-w-3xl">
          <p className="section-eyebrow !text-brand-gold">{t.eyebrow}</p>
          <h2 className="mt-4 text-3xl font-bold leading-tight tracking-[-0.03em] sm:text-4xl md:text-5xl">{t.title}</h2>
          <p className="mt-4 text-base leading-relaxed text-white/75 sm:text-lg">{t.lead}</p>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {KEY_STATS.map((s, i) => (
            <StatTile key={i} s={s} locale={locale} i={i} />
          ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <figure className="min-w-0 rounded-3xl bg-white p-5 text-brand-charcoal shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)] sm:p-7">
            <figcaption>
              <p className="text-lg font-bold text-brand-navy">{t.c1}</p>
              <p className="mt-1 text-sm text-brand-muted">{t.c1s}</p>
            </figcaption>
            <div className="mt-4">
              <ForeignWorkersChart locale={locale} />
            </div>
            <a href={SOURCES[1].url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-[11px] text-brand-muted hover:text-brand-green hover:underline">
              {locale === 'ja' ? '出典：厚生労働省「外国人雇用状況」' : 'Source: Ministry of Health, Labour and Welfare (MHLW)'}
            </a>
          </figure>
          <figure className="min-w-0 rounded-3xl bg-white p-5 text-brand-charcoal shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)] sm:p-7">
            <figcaption>
              <p className="text-lg font-bold text-brand-navy">{t.c2}</p>
              <p className="mt-1 text-sm text-brand-muted">{t.c2s}</p>
            </figcaption>
            <div className="mt-4">
              <WorkingAgeChart locale={locale} />
            </div>
            <a href={SOURCES[3].url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-[11px] text-brand-muted hover:text-brand-green hover:underline">
              {locale === 'ja' ? '出典：国勢調査、国立社会保障・人口問題研究所（2023年推計）' : 'Source: Census; National Institute of Population and Social Security Research (2023 projection)'}
            </a>
          </figure>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {MORE_FACTS.map((f) => (
            <a
              key={f.value}
              href={SOURCES[f.source].url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition-colors hover:border-brand-gold/50 hover:bg-white/[0.07]"
            >
              <span className="text-2xl font-bold tracking-tight text-brand-gold tabular-nums">{f.value}</span>
              <span className="text-sm leading-relaxed text-white/75">{f.label[locale]}</span>
            </a>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start justify-between gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center">
          <details className="text-xs text-white/50">
            <summary className="cursor-pointer font-semibold text-white/70 hover:text-white">{t.sources}</summary>
            <ul className="mt-2 space-y-1">
              {SOURCES.map((s) => (
                <li key={s.url}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-brand-gold hover:underline">
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
          </details>
          <Link href={`/${locale}/work-in-japan`} className="btn-primary">
            {t.cta} <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  )
}
